import { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { authService } from '@/auth/hooks';
import { tokenStore } from '@/auth/token-store';
import { Brand } from '@/components/brand';
import { TrialBanner } from '@/components/first-run-card';
import { BillingWall, useBillingGate } from '@/components/billing-wall';
import { BillingPage } from '@/pages/billing';
import { Button } from '@/components/ui/button';
import { FleetSwitcher } from '@/components/fleet-switcher';
import { FeedbackDialog } from '@/components/feedback-dialog';
import { fleetStore } from '@/lib/fleet-store';
import { useFleets } from '@/data/fleets';
import { useAdminHostTenant } from '@/data/admin-host';
import { isTenantAdminHost } from '@/config';
import { Spinner } from '@/components/ui/spinner';
import { NoFleetsPage } from '@/pages/no-fleets';
import { cn } from '@/lib/cn';

/** Phase A U3: true while the current access token carries the imp claim —
 *  an admin's 30-minute impersonation session. Purely presentational (the
 *  claim is server-signed); shows the banner so nobody forgets whose eyes
 *  they're borrowing. */
function isImpersonating(): boolean {
  const t = tokenStore.getAccess();
  if (!t) return false;
  try {
    const payload = JSON.parse(
      atob(t.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')),
    ) as { imp?: string };
    return typeof payload.imp === 'string';
  } catch {
    return false;
  }
}

const NAV: Array<{ label: string; to: string; soon?: boolean }> = [
  { label: 'Loads', to: '/' },
  { label: 'Load board', to: '/load-postings' },
  { label: 'Team', to: '/settings/team' },
  { label: 'Vehicles', to: '/vehicles' },
  { label: 'Live map', to: '/map' },
  { label: 'Customers', to: '/customers' },
  { label: 'Website', to: '/website' },
  { label: 'Invoices', to: '/invoices' },
  { label: 'Statistics', to: '/statistics' },
  { label: 'Billing', to: '/settings/billing' },
  { label: 'Support', to: '/support' },
  { label: 'Settings', to: '/settings' },
];

export function Shell() {
  const navigate = useNavigate();
  const router = useRouterState({ select: (s) => s.location.pathname });
  const fleetsQuery = useFleets();
  const billingGate = useBillingGate();
  // FAD A3: on admin.<zone> the header carries the fleet's identity — the
  // admin surface says who runs it, unlike customer surfaces (zero-Breaker).
  const adminTenant = useAdminHostTenant();
  const hostTenant = isTenantAdminHost() ? (adminTenant.data ?? null) : null;

  // C6: below lg the sidebar collapses to an off-canvas drawer behind a
  // hamburger in the top bar. Navigating closes it.
  const [navOpen, setNavOpen] = useState(false);
  // V2-U7: "Contact support" — low-threshold feedback, help-menu slot.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  useEffect(() => {
    setNavOpen(false);
  }, [router]);

  async function handleLogout() {
    await authService.logout();
    fleetStore.clear();
    await navigate({ to: '/login' });
  }

  // While we're still resolving the user's fleet list we render a small
  // splash so the rest of the UI doesn't flicker.
  if (fleetsQuery.isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-breaker-ground">
        <div className="h-1 bg-breaker-identity" />
        <div className="flex-1 grid place-items-center">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  // No fleets at all — full-page hand-off.
  if ((fleetsQuery.data?.length ?? 0) === 0) {
    return <NoFleetsPage />;
  }

  // P2 card wall: trial over, no card — full screen, nothing underneath.
  if (billingGate.gate === 'wall' && billingGate.state) {
    return <BillingWall state={billingGate.state} />;
  }

  // One nav, two homes: the fixed desktop sidebar and the mobile drawer.
  const sidebarNav = (
    <nav className="px-3 space-y-1">
      {NAV.map((item) => {
        const active =
          item.to === '/' ? router === '/' : router.startsWith(item.to);
        const className = cn(
          'flex items-center justify-between rounded-md px-3 py-2 text-sm',
          active
            ? 'bg-breaker-ground-2 text-breaker-ink font-medium'
            : 'text-breaker-ink-2 hover:bg-breaker-ground-2',
          item.soon && 'cursor-not-allowed opacity-50 hover:bg-transparent',
        );
        const content = (
          <>
            <span>{item.label}</span>
            {item.soon ? (
              <span className="text-2xs text-breaker-mute uppercase tracking-wider">
                Soon
              </span>
            ) : null}
          </>
        );
        if (item.soon) {
          return (
            <div key={item.to} className={className} aria-disabled>
              {content}
            </div>
          );
        }
        return (
          <Link key={item.to} to={item.to} className={className}>
            {content}
          </Link>
        );
      })}
    </nav>
  );

  return (
    // overflow-x-hidden: the app shell is the guard — no child may widen
    // the document (the mobile pan-right bug: sideways document scroll
    // left the sticky header spanning only the viewport).
    <div className="min-h-screen flex flex-col bg-breaker-ground overflow-x-hidden">
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-white/85 backdrop-blur border-b border-breaker-line">
        <div
          className="h-1 bg-breaker-identity"
          style={
            hostTenant?.brand.primaryHex
              ? { backgroundColor: hostTenant.brand.primaryHex }
              : undefined
          }
        />
        <TrialBanner />
        {isImpersonating() ? (
          <div className="bg-breaker-red px-4 py-1.5 text-center text-xs font-medium text-white">
            Impersonation session — you are acting as this member. It ends itself within 30
            minutes (or on refresh).
          </div>
        ) : null}
        <div className="flex items-center gap-3 sm:gap-6 px-4 sm:px-6 h-14 min-w-0">
          {/* C6: hamburger — only below lg, where the sidebar is a drawer. */}
          <button
            type="button"
            aria-label="Open navigation"
            onClick={() => setNavOpen(true)}
            className="lg:hidden -ml-2 rounded-md p-2 text-breaker-ink-2 hover:bg-breaker-ground-2 hover:text-breaker-ink"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M2 4.5h14M2 9h14M2 13.5h14" />
            </svg>
          </button>
          <Link to="/" className="flex items-center gap-2 shrink-0">
            {hostTenant ? (
              <>
                {hostTenant.brand.logoUrl ? (
                  <img
                    src={hostTenant.brand.logoUrl}
                    alt=""
                    className="h-6 w-auto max-w-[120px] object-contain"
                  />
                ) : null}
                <span className="text-sm font-semibold text-breaker-ink truncate max-w-[160px]">
                  {hostTenant.fleetName}
                </span>
                <span className="hidden sm:inline-flex items-baseline gap-1 text-2xs text-breaker-mute whitespace-nowrap">
                  — powered by <Brand size="sm" />
                </span>
              </>
            ) : (
              <>
                <Brand size="md" />
                <span className="hidden sm:inline text-xs text-breaker-mute uppercase tracking-wider">
                  Dispatch
                </span>
              </>
            )}
          </Link>

          <span className="hidden sm:block h-5 w-px bg-breaker-line" />
          <FleetSwitcher />

          <div className="flex-1" />

          {/* Below md the actions live in the drawer — never clipped. */}
          <Button
            variant="ghost"
            size="sm"
            className="hidden md:inline-flex"
            onClick={() => setFeedbackOpen(true)}
          >
            Contact support
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="hidden md:inline-flex"
            onClick={handleLogout}
          >
            Sign out
          </Button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — fixed on desktop, hidden below lg (drawer takes over). */}
        <aside className="hidden lg:block w-56 border-r border-breaker-line bg-white py-4">
          {sidebarNav}
        </aside>

        {/* C6: off-canvas drawer below lg. */}
        {navOpen ? (
          <div className="fixed inset-0 z-30 lg:hidden">
            <div
              className="absolute inset-0 bg-breaker-ink/30"
              aria-hidden="true"
              onClick={() => setNavOpen(false)}
            />
            <aside className="absolute inset-y-0 left-0 w-64 max-w-[80vw] overflow-y-auto border-r border-breaker-line bg-white py-4 shadow-xl">
              <div className="flex items-center justify-between px-4 pb-3 mb-1 border-b border-breaker-line">
                <Brand size="md" />
                <button
                  type="button"
                  aria-label="Close navigation"
                  onClick={() => setNavOpen(false)}
                  className="rounded-md p-2 text-breaker-ink-2 hover:bg-breaker-ground-2 hover:text-breaker-ink"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="M3 3l10 10M13 3L3 13" />
                  </svg>
                </button>
              </div>
              {sidebarNav}
              {/* Top-bar actions relocate here below md (they'd clip). */}
              <div className="mt-4 border-t border-breaker-line px-2 pt-3 md:hidden">
                <button
                  type="button"
                  onClick={() => {
                    setNavOpen(false);
                    setFeedbackOpen(true);
                  }}
                  className="flex w-full items-center rounded-md px-3 py-2 text-sm text-breaker-ink-2 hover:bg-breaker-ground-2"
                >
                  Contact support
                </button>
                <button
                  type="button"
                  onClick={() => void handleLogout()}
                  className="flex w-full items-center rounded-md px-3 py-2 text-sm text-breaker-ink-2 hover:bg-breaker-ground-2"
                >
                  Sign out
                </button>
              </div>
            </aside>
          </div>
        ) : null}

        {/* Main. P2: a suspended fleet is gated to the billing screen —
            the shell chrome stays (fleet switcher, sign out) but every
            route renders billing until the webhook restores the fleet. */}
        {/* min-w-0: without it this flex child refuses to shrink below its
            content width and a wide table widens the whole page. */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          {billingGate.gate === 'suspended' ? <BillingPage /> : <Outlet />}
        </main>
      </div>
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
}
